<script setup lang="ts">
import type { Music } from '~/services/musicService'
import { usePlayerStore } from '~/stores/player'
import MmIcon from '../base/MmIcon.vue'
import MmDialog from '../base/MmDialog.vue'
import { sendSongEvent, type PlaybackSurface } from '~/services/songPlayback'
import type { ListSong } from './rankings'

const props = defineProps<{ tracks: ListSong[]; title: string; surface?: PlaybackSurface; shuffle?: boolean; compact?: boolean }>()
const { t } = useI18n()
const player = usePlayerStore()
const route = useRoute()
const siteUrl = String(useRuntimeConfig().public.siteUrl || 'https://musicmint.ai').replace(/\/$/, '')
const shareOpen = ref(false)
const copied = ref(false)
const copyFailed = ref(false)
const shareUrl = computed(() => `${siteUrl}${route.fullPath}`)
const playable = computed(() => props.tracks.filter(track => track.audio_url))
const playAll = (random = false) => {
  if (!playable.value.length) return
  const queue = [...playable.value]
  if (random) for (let index = queue.length - 1; index > 0; index--) {
    const other = Math.floor(Math.random() * (index + 1))
    ;[queue[index], queue[other]] = [queue[other], queue[index]]
  }
  const song = queue[0]
  const context = { ...song.listContext, surface: props.surface || 'chart', position: song.listContext?.position || 1 }
  void sendSongEvent(song.id, 'click', context)
  player.play(song, context, { queue })
}
const copy = async () => {
  try { await navigator.clipboard.writeText(shareUrl.value); copied.value = true; copyFailed.value = false }
  catch { copied.value = false; copyFailed.value = true }
}
const openShare = () => { copied.value = false; copyFailed.value = false; shareOpen.value = true }
</script>

<template>
  <div class="cluster collection-actions" :class="{ 'is-compact': compact }">
    <button class="btn collection-play" :class="compact ? 'btn-primary' : 'btn-live'" type="button" :disabled="!playable.length" :aria-label="`${t('mm.explore.playAll')}: ${title}`" @click="playAll()"><MmIcon name="play" :size="16" /><span>{{ t('mm.explore.playAll') }}</span></button>
    <button v-if="shuffle" class="btn btn-outline collection-shuffle" type="button" :disabled="!playable.length" :aria-label="`${t('ux.shuffle')}: ${title}`" @click="playAll(true)"><MmIcon name="shuffle" :size="16" /><span>{{ t('ux.shuffle') }}</span></button>
    <slot />
    <button class="btn btn-outline collection-share" type="button" :aria-label="`${t('mm.explore.share')}: ${title}`" @click="openShare"><MmIcon name="share" :size="16" /><span>{{ t('mm.explore.share') }}</span></button>
  </div>
  <MmDialog v-model="shareOpen" :title="`${t('mm.explore.share')} · ${title}`">
    <div class="collection-share-content">
      <label>{{ t('ux.shareLink') }}<input :value="shareUrl" readonly @focus="($event.target as HTMLInputElement).select()"></label>
      <p v-if="copied" role="status">{{ t('ux.copied') }}</p>
      <p v-if="copyFailed" role="alert">{{ t('ux.shareCopyFailed') }}</p>
      <button class="btn btn-primary" type="button" @click="copy">{{ t('ux.copyLink') }}</button>
    </div>
  </MmDialog>
</template>

<style scoped>
.collection-actions.cluster { display: flex; flex-wrap: wrap; align-items: stretch; gap: var(--s-2); }
.collection-actions .btn { font-size: 15px; }
.collection-actions > .btn { display: inline-flex; flex: 1 1 0; align-items: center; justify-content: center; min-width: 0; min-height: 44px; padding: var(--s-2) var(--s-3); white-space: normal; text-align: center; line-height: 1.3; }
.collection-actions > .btn > span { min-width: 0; overflow-wrap: anywhere; }
.collection-actions > .btn > :deep(.ic) { flex-shrink: 0; }
@media(max-width:359px) { .collection-actions > .btn { flex-basis: 100%; } }
.mm-root .collection-play { background: var(--media-ink); color: var(--premium-ink); }
.mm-root .collection-share { border-color: color-mix(in srgb, var(--media-ink) 40%, transparent); color: var(--media-ink); }
.collection-actions.is-compact { flex-wrap: nowrap; gap: var(--s-1); }
.collection-actions.is-compact > .btn { flex: 0 0 auto; min-width: max-content; padding-inline: var(--s-2); font-size: var(--t-small); white-space: nowrap; }
.collection-actions.is-compact > .btn > span { white-space: nowrap; overflow-wrap: normal; }
.collection-actions.is-compact > :is(.collection-shuffle, .collection-share) { width: 44px; min-width: 44px; padding-inline: 0; }
.collection-actions.is-compact > :is(.collection-shuffle, .collection-share) > span { display: none; }
.mm-root .collection-actions.is-compact .collection-play { background: var(--accent); color: var(--accent-ink); }
.mm-root .collection-actions.is-compact .btn-outline { color: var(--text); border-color: var(--line-strong); }
.collection-share-content, .collection-share-content label { display: grid; gap: var(--s-3); }
.collection-share-content input { width: 100%; min-width: 0; min-height: 44px; padding: var(--s-3); color: var(--text); background: var(--surface-2); border: 1px solid var(--line-strong); border-radius: var(--r-sm); font-size: 16px; }
</style>
